import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { getCountryFlagUrl } from '@pickleballinc/lib/getCountryFlagUrl';
import { RankingStatusBadge } from '@pickleballinc/ui/components/global/RankingStatusBadge/RankingStatusBadge';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';

import { publicEnv } from '@/lib/env';
import { PlayerServerDots } from '@/modules/results/components/PlayerServerDots';
import { MatchPlayerAvatar } from '@/modules/results/match/MatchPlayerAvatar';
import { H2HStats, ResultMatchType, ResultsMatchTeamData } from '@/modules/results/types';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';

interface MatchPlayersProps {
  match: ResultMatchType;
  team: 1 | 2;
  matchH2HData?: H2HStats;
}

function showSeed(
  in_bracket_type: string,
  show_seed_on_bracket_be: boolean,
  show_seed_on_bracket_pr: boolean
): boolean {
  if (['W', 'L1', 'GS', 'GS2', 'B'].includes(in_bracket_type.toUpperCase())) {
    return show_seed_on_bracket_be;
  } else if (['RR', 'SRR'].includes(in_bracket_type.toUpperCase())) {
    return show_seed_on_bracket_pr;
  }
  return false;
}
export const MatchPlayers = ({ match, team, matchH2HData }: MatchPlayersProps) => {
  const teamMap: Record<1 | 2, ResultsMatchTeamData> = {
    1: {
      player_one_name: 'team_one_player_one_name',
      player_two_name: 'team_one_player_two_name',
      winning_percentage: 'team_one_winning_percentage',
      team_one_player_one_picture: 'team_one_player_one_picture',
      team_one_player_two_picture: 'team_one_player_two_picture',
      player_one_abbr: 'team_one_player_one_representing_country_two_digit_abbreviation',
      player_two_abbr: 'team_one_player_two_representing_country_two_digit_abbreviation',
      player_one_country: 'team_one_player_one_country_two_digit_abbrviation',
      player_two_country: 'team_one_player_two_country_two_digit_abbrviation',
      in_bracket_type: 'in_bracket_type',
      show_seed_on_bracket_be: 'show_seed_on_bracket_be',
      show_seed_on_bracket_pr: 'show_seed_on_bracket_pr',
      seed: 'team_one_seed',
      p1_slug: 'team_one_player_one_slug',
      p2_slug: 'team_one_player_two_slug',
      p1_proLevelId: 'team_one_player_one_pro_level_id',
      p2_proLevelId: 'team_one_player_two_pro_level_id',
      p1_wprBadge: 'team_one_player_one_wpr_badge',
      p2_wprBadge: 'team_one_player_two_wpr_badge',
    },
    2: {
      player_one_name: 'team_two_player_one_name',
      player_two_name: 'team_two_player_two_name',
      winning_percentage: 'team_one_winning_percentage',
      team_one_player_one_picture: 'team_two_player_one_picture',
      team_one_player_two_picture: 'team_two_player_two_picture',
      player_one_abbr: 'team_two_player_one_representing_country_two_digit_abbreviation',
      player_two_abbr: 'team_two_player_two_representing_country_two_digit_abbreviation',
      player_one_country: 'team_two_player_one_country_two_digit_abbrviation',
      player_two_country: 'team_two_player_two_country_two_digit_abbrviation',
      in_bracket_type: 'in_bracket_type',
      show_seed_on_bracket_be: 'show_seed_on_bracket_be',
      show_seed_on_bracket_pr: 'show_seed_on_bracket_pr',
      seed: 'team_two_seed',
      p1_slug: 'team_two_player_one_slug',
      p2_slug: 'team_two_player_two_slug',
      p1_proLevelId: 'team_two_player_one_pro_level_id',
      p2_proLevelId: 'team_two_player_two_pro_level_id',
      p1_wprBadge: 'team_two_player_one_wpr_badge',
      p2_wprBadge: 'team_two_player_two_wpr_badge',
    },
  };

  const {
    player_one_name,
    player_two_name,
    team_one_player_one_picture,
    team_one_player_two_picture,
    player_one_abbr,
    player_two_abbr,
    player_one_country,
    player_two_country,
    in_bracket_type,
    show_seed_on_bracket_be,
    show_seed_on_bracket_pr,
    seed,
    p1_slug,
    p2_slug,
    p1_proLevelId,
    p2_proLevelId,
    p1_wprBadge,
    p2_wprBadge,
  } = teamMap[team];

  const p1Name = match[player_one_name] ? String(match[player_one_name]) : '';
  const p2Name = match[player_two_name] ? String(match[player_two_name]) : '';
  const p1Badge = match[p1_wprBadge];
  const p2Badge = match[p2_wprBadge];
  const p1Img = match[team_one_player_one_picture]
    ? String(match[team_one_player_one_picture])
    : '';
  const p2Img = match[team_one_player_two_picture]
    ? String(match[team_one_player_two_picture])
    : '';
  const p1Flag =
    matchH2HData && matchH2HData[player_one_abbr]
      ? getCountryFlagUrl(String(matchH2HData[player_one_abbr]), 32, 32)
      : '';
  const p2Flag =
    matchH2HData && matchH2HData[player_two_abbr]
      ? getCountryFlagUrl(String(matchH2HData[player_two_abbr]), 32, 32)
      : '';
  const p1Country =
    matchH2HData && matchH2HData[player_one_country]
      ? getCountryFlagUrl(String(matchH2HData[player_one_country]), 32, 32)
      : '';
  const p2Country =
    matchH2HData && matchH2HData[player_two_country]
      ? getCountryFlagUrl(String(matchH2HData[player_two_country]), 32, 32)
      : '';
  const bracketType = match[in_bracket_type] ? String(match[in_bracket_type]) : '';
  const bracketBe = match[show_seed_on_bracket_be]
    ? Boolean(match[show_seed_on_bracket_be])
    : false;
  const bracketPr = match[show_seed_on_bracket_pr]
    ? Boolean(match[show_seed_on_bracket_pr])
    : false;
  const p1Seed = match[seed] || '';
  const p2Seed = match[seed] || '';

  const p1Slug = match[p1_slug] || '';
  const p2Slug = match[p2_slug] || '';

  const p1ProLevelId = match[p1_proLevelId] || 0;
  const p2ProLevelId = match[p2_proLevelId] || 0;

  const imgSize = p1Name && p2Name ? 'w-16 h-16 sm:w-28 sm:h-28' : 'w-24 h-24 sm:w-48 sm:h-48';
  const badgeSize = p1Name && p2Name ? 'w-6 h-6 sm:w-10 sm:h-10' : 'w-8 h-8 sm:w-14 sm:h-14';
  const flagSize = p1Name && p2Name ? 'h-5 w-5 sm:h-8 sm:w-8' : 'h-6 w-6 sm:h-8 sm:w-8';
  const flagWrapperSize =
    p1Name && p2Name
      ? 'bottom-[-8px] left-[33%] sm:bottom-[-9px] sm:left-[35%]'
      : 'bottom-[-8px] left-[36%] sm:bottom-[-10px] sm:left-[40%]';

  const flagClassName = clsx('relative', flagSize);
  const flagWrapperClassName = clsx('absolute text-sm sm:text-lg', flagWrapperSize);

  const p1IsServer =
    team === 1
      ? match.server === 1 && match.server_from_team === 1
      : match.server === 2 && match.server_from_team === 1;
  const p2IsServer =
    team === 1
      ? match.server === 1 && match.server_from_team === 2
      : match.server === 2 && match.server_from_team === 2;
  const secondServerDot =
    team === 1
      ? match.server === 1 && match.current_serving_number === 2
      : match.server === 2 && match.current_serving_number === 2;

  return (
    <div className='flex flex-col items-center justify-center gap-3 sm:gap-8 text-xs font-semibold text-white sm:text-[15px]'>
      {p1Name !== '' && (
        <div className='flex flex-col-reverse'>
          <CustomLink className='flex flex-row hover:text-blue-400' href={`/players/${p1Slug}`}>
            <div className='flex flex-col items-center'>
              <div
                className={clsx(
                  'relative',
                  imgSize,
                  p1Img === '' ? '' : 'rounded-full bg-experiment/50'
                )}
              >
                <MatchPlayerAvatar
                  name={p1Name}
                  img={p1Img}
                  proLevelId={p1ProLevelId}
                  isWinner={match?.winner === team}
                  isSingles={p2Name === ''}
                />
                {p1Flag !== '' ? (
                  <div className={flagWrapperClassName}>
                    <div className={flagClassName}>
                      <img
                        src={p1Flag}
                        alt={`${p1Flag} flag`}
                        className='object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                ) : p1Country !== '' ? (
                  <div className={flagWrapperClassName}>
                    <div className={flagClassName}>
                      <img
                        src={p1Country}
                        alt={`${p1Country} flag`}
                        className='object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                ) : null}
                {match?.winner === team && (
                  <div className='absolute bottom-0 right-0 text-sm sm:text-lg'>
                    <div className={`relative ${badgeSize}`}>
                      <img
                        src={getCDNURL(
                          `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/Badge.svg`,
                          40,
                          40
                        )}
                        alt='pickleball'
                        className='object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className='flex flex-col mt-[11px] text-sm sm:text-lg'>
                {p2Name === '' && showSeed(bracketType, bracketBe, bracketPr) ? (
                  <h6 className='flex justify-center text-sm sm:text-lg mb-2'>{`(${p1Seed})`}</h6>
                ) : null}
                <span>{p1Name}</span>
                <div className='flex flex-col sm:flex-row mt-2 sm:mt-[11px] gap-2 sm:gap-[14px] justify-center items-center'>
                  {p1Badge && FEATURE_FLAGS.ENABLE_RANKING_BADGES ? (
                    <RankingStatusBadge
                      badgeRank={p1Badge.rank}
                      rankingStatus={p1Badge.badge_title}
                      showBadge={p1Badge.show_badge_title}
                      size='sm'
                      badgeCenter
                    />
                  ) : null}
                  <PlayerServerDots
                    isServer={p1IsServer}
                    showSecondDot={secondServerDot}
                    matchStatus={match.match_status}
                  />
                </div>
              </div>
            </div>
          </CustomLink>
        </div>
      )}
      {p2Name !== '' && (
        <div className='flex flex-col gap-1'>
          {showSeed(bracketType, bracketBe, bracketPr) && (
            <h6 className='text-sm sm:text-lg'>{`(${p2Seed})`}</h6>
          )}
          <CustomLink className='hover:text-blue-400' href={`/players/${p2Slug}`}>
            <div className='flex flex-col items-center'>
              <div
                className={clsx(
                  'relative',
                  imgSize,
                  p2Img === '' ? '' : 'rounded-full bg-experiment/50'
                )}
              >
                <MatchPlayerAvatar
                  name={p2Name}
                  img={p2Img}
                  proLevelId={p2ProLevelId}
                  isWinner={match?.winner === team}
                  isSingles={false}
                />
                {p2Flag !== '' ? (
                  <div className={flagWrapperClassName}>
                    <div className={flagClassName}>
                      <img
                        src={p2Flag}
                        alt={`${p2Flag} flag`}
                        className='object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                ) : p2Country !== '' ? (
                  <div className={flagWrapperClassName}>
                    <div className={flagClassName}>
                      <img
                        src={p2Country}
                        alt={`${p2Country} flag`}
                        className='rounded-full object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                ) : null}
                {match?.winner === team && (
                  <div className='absolute bottom-0 right-0 text-sm sm:text-lg'>
                    <div className={`relative ${badgeSize}`}>
                      <img
                        src={getCDNURL(
                          `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/Badge.svg`,
                          40,
                          40
                        )}
                        alt='pickleball'
                        className='rounded-full object-cover absolute top-0 left-0'
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className='mt-[11px] text-sm sm:text-lg'>
                <span>{p2Name}</span>
                <div className='flex flex-col sm:flex-row mt-2 sm:mt-[11px] gap-2 sm:gap-[14px] justify-center items-center'>
                  {p2Badge && FEATURE_FLAGS.ENABLE_RANKING_BADGES ? (
                    <RankingStatusBadge
                      badgeRank={p2Badge.rank}
                      rankingStatus={p2Badge.badge_title}
                      showBadge={p2Badge.show_badge_title}
                      badgeCenter
                      size='sm'
                    />
                  ) : null}
                  <PlayerServerDots
                    isServer={p2IsServer}
                    showSecondDot={secondServerDot}
                    matchStatus={match.match_status}
                  />
                </div>
              </div>
            </div>
          </CustomLink>
        </div>
      )}
      {match?.winner === team ? (
        <div className='text-base uppercase sm:text-lg'>
          <p>{p1Name !== '' && p2Name !== '' ? 'Winners' : 'Winner'}</p>
        </div>
      ) : (
        <div className='h-6 sm:h-[28px]' />
      )}
    </div>
  );
};
